import { memo, useState } from "react";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { EntityAssignmentModal } from "../EntityAssignmentModal";
import { EntityLabel } from "../EntityLabel";
import { useOrganizationIntegrations } from "../OrganizationIntegrationsContext";
import styles from "../IntegrationsPage.module.scss";

export const EntityCell = memo(
    ({ item }: { item: OrganizationIntegrationAccountItem }) => {
        const { account } = item;
        const { pendingByAccountId, workspaces } =
            useOrganizationIntegrations();
        const [isAssigning, setIsAssigning] = useState(false);

        // `entity` can be null for a client the caller can't access — the
        // account is still assigned, so the cell stays read-only rather than
        // offering a change the caller has no options for.
        if (account.entityId !== null && account.entity === null) {
            return <span>-</span>;
        }

        const pending = pendingByAccountId[account.id];
        const workspaceId =
            account.entity?.workspaceId ?? pending?.workspaceId ?? null;

        // An entity only means something inside a client, so until one is
        // picked this cell is a placeholder rather than a disabled control the
        // user might keep clicking.
        if (workspaceId === null) {
            return <span>-</span>;
        }

        // A staged entity reads exactly like an assigned one — on a surface
        // that commits through its own Confirm, the row has to show the pick
        // that is about to be sent, not the emptiness it is replacing.
        const stagedEntity = workspaces
            .find((workspace) => workspace.id === workspaceId)
            ?.entities.find((entity) => entity.id === pending?.entityId);
        const shownEntity = stagedEntity
            ? { ...stagedEntity, workspaceId }
            : account.entity;

        return (
            <>
                {/* An assigned entity can still be swapped for another of the
                    same client, so it reads as text until the row is hovered
                    rather than being locked outright. */}
                <EditableCellContent
                    className={styles.assignmentCellValue}
                    onClick={() => setIsAssigning(true)}
                >
                    {shownEntity ? (
                        <EntityLabel entity={shownEntity} />
                    ) : (
                        "Select entity"
                    )}
                </EditableCellContent>
                {isAssigning && (
                    <EntityAssignmentModal
                        accountIds={[account.id]}
                        workspaceId={workspaceId}
                        initialEntityId={shownEntity?.id ?? null}
                        onClose={() => setIsAssigning(false)}
                    />
                )}
            </>
        );
    },
);

EntityCell.displayName = "EntityCell";
